import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import Modal from "./Modal";
import "../styles/KpiFormModal.css";
import "../styles/Modal.css";

export default function InputTabunganModal({ user, onClose }) {
  const API = process.env.REACT_APP_API_BASE_URL || "";

  // ===== STATE UTAMA =====
  const [tabunganOpen, setTabunganOpen] = useState(false);
  const [submittedTabungan, setSubmittedTabungan] = useState([]);
  const [masterData, setMasterData] = useState([]);

  const [form, setForm] = useState({
    nama: "",
    divisi: "",
    unit: "",
    kerja_tabungan_gaji: "",
    hasil_berwujud: "",
    konversi_uang: "",
    parameter: "",
    target: "",
    actual: "",
    nilai: "",
    bobot: "",
  });

  // ===== FILE STATES =====
  const [buktiNilaiPreview, setBuktiNilaiPreview] = useState(null);
  const [buktiNilaiName, setBuktiNilaiName] = useState("");
  const [buktiNilaiFile, setBuktiNilaiFile] = useState(null);
  const [buktiIsPDF, setBuktiIsPDF] = useState(false);

  // ===== SIGNATURE =====
  const [drawing, setDrawing] = useState("");
  const canvasRef = useRef(null);

  // ===== DROPDOWN & SEARCH =====
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filteredList, setFilteredList] = useState([]);
  const [batchList, setBatchList] = useState([]);

  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const namaDropdownRef = useRef(null); // 🔥 Deteksi klik di luar

  // ===== LOADING & MODAL =====
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingText, setLoadingText] = useState("Memproses...");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("info");

  // ===== HELPER MODAL =====
  const showModal = (title, message, type = "info") => {
    setModalTitle(title);
    setModalMessage(message);
    setModalType(type);
    setModalOpen(true);
  };

  const showFinishModal = () => {
    setModalTitle("Selesai");
    setModalMessage("Terima kasih. Data Tabungan berhasil disimpan.");
    setModalType("finish");
    setModalOpen(true);
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = "Data sedang disimpan. Yakin ingin keluar?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [loading]);

  // 🔥 Klik di luar untuk menutup dropdown nama
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (namaDropdownRef.current && !namaDropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ========================= LOAD DATA =========================
  useEffect(() => {
    axios
      .get(`${API}/api/tabungan-data`)
      .then((res) => {
        if (res.data.result === "success") setMasterData(res.data.message);
      })
      .catch((err) => console.error("Error load master:", err));
  }, [API]);

  // 🔥 MENGGUNAKAN USEMEMO AGAR PENCARIAN SUPER RINGAN & REAKTIF 🔥
  const uniqueNames = useMemo(() => {
    return [...new Set(masterData.map((item) => item.nama).filter(Boolean))].sort();
  }, [masterData]);

  const filteredNames = useMemo(() => {
    if (!form.nama || form.nama.length < 3) return [];
    return uniqueNames.filter((nama) =>
      nama.toLowerCase().includes(form.nama.toLowerCase())
    );
  }, [form.nama, uniqueNames]);

  // ========================= FETCH SUBMITTED TABUNGAN =========================
  const fetchSubmittedTabungan = async (namaKaryawan) => {
    try {
      const res = await axios.post(`${API}/api/tabungan-submitted`, {
        email: user?.email,
        nama: namaKaryawan,
      });
      if (res.data.result === "success") {
        setSubmittedTabungan(res.data.data || []);
      }
    } catch (err) {
      console.error("Error load submitted Tabungan:", err);
    }
  };

  // ========================= HANDLER NAMA (WIN-WIN SOLUTION) =========================
  const handleNamaFocus = () => {
    setDropdownOpen(true);
  };

  const handleNamaChange = (e) => {
    const namaInput = e.target.value;
    setForm((prev) => ({ ...prev, nama: namaInput }));
    setSubmittedTabungan([]); // Reset daftar selesai saat ketik nama baru
    setDropdownOpen(true);

    if (namaInput.trim() === "") {
      resetFormDetail();
    }
  };

  const handleSelectNama = (namaTerpilih) => {
    const dataNama = masterData.find((d) => d.nama === namaTerpilih);
    if (!dataNama) return;

    setForm({
      ...form,
      nama: dataNama.nama,
      divisi: dataNama.divisi,
      unit: dataNama.unit,
      kerja_tabungan_gaji: "",
      hasil_berwujud: "",
      konversi_uang: "",
      parameter: "",
      target: "",
      actual: "",
      nilai: "",
      bobot: "",
    });
    setDropdownOpen(false);
    fetchSubmittedTabungan(namaTerpilih);

    const filtered = masterData.filter((d) => d.nama === namaTerpilih);
    setFilteredList(filtered);
  };

  const resetFormDetail = () => {
    setForm((prev) => ({
      ...prev,
      divisi: "",
      unit: "",
      kerja_tabungan_gaji: "",
      hasil_berwujud: "",
      konversi_uang: "",
      parameter: "",
      target: "",
      actual: "",
      nilai: "",
      bobot: "",
    }));
  };

  const handleTabunganChange = (item) => {
    if (typeof item === "string") {
      setForm({ ...form, kerja_tabungan_gaji: item });
      return;
    }

    setForm({
      ...form,
      kerja_tabungan_gaji: item.kerja_tabungan_gaji,
      hasil_berwujud: item.hasil_berwujud || "",
      konversi_uang: item.konversi_uang || "",
      parameter: item.parameter || "",
      target: item.target || "",
      nilai: item.nilai || "",
      bobot: formatBobot(item.bobot),
    });
    setTabunganOpen(false);
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // ========================= FILE HANDLING =========================
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    setBuktiIsPDF(false);

    if (!file) {
      setBuktiNilaiPreview(null);
      setBuktiNilaiName("");
      setBuktiNilaiFile(null);
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      showModal("Perhatian", "File harus berupa JPG, JPEG, PNG, atau PDF.", "error");
      e.target.value = null;
      return;
    }

    setBuktiNilaiName(file.name);
    setBuktiNilaiFile(file);

    if (file.type === "application/pdf") {
      setBuktiIsPDF(true);
      setBuktiNilaiPreview(URL.createObjectURL(file));
    } else {
      setBuktiNilaiPreview(URL.createObjectURL(file));
    }
  };

  // ========================= BATCH LOGIC =========================
  const handleAddBatch = () => {
    if (!form.kerja_tabungan_gaji || !form.actual) {
      showModal("Perhatian", "Pilih item tabungan dan isi nilai actual.", "error");
      return;
    }

    if (!buktiNilaiFile) {
      showModal("Perhatian", "Bukti nilai wajib diunggah.", "error");
      return;
    }

    const exists = batchList.some(
      (b) => b.kerja_tabungan_gaji === form.kerja_tabungan_gaji && b.parameter === form.parameter,
    );
    if (exists) {
      showModal("Perhatian", "Item ini sudah ditambahkan ke daftar.", "error");
      return;
    }

    setBatchList((prev) => [
      ...prev,
      {
        ...form,
        bukti_nilai_name: buktiNilaiName,
        bukti_file_obj: buktiNilaiFile,
        target: form.target || "",
        bukti_pdf: buktiIsPDF,
      },
    ]);

    setForm({
      ...form,
      kerja_tabungan_gaji: "",
      hasil_berwujud: "",
      konversi_uang: "",
      parameter: "",
      actual: "",
      target: "",
      nilai: "",
      bobot: "",
    });

    setBuktiNilaiPreview(null);
    setBuktiNilaiName("");
    setBuktiNilaiFile(null);
    setBuktiIsPDF(false);
    if (fileRef.current) fileRef.current.value = null;
  };

  // ========================= SUBMIT LOGIC =========================
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadSingleFileToGAS = async (file) => {
    let fileToUpload = file;
    if (file.type.startsWith("image/")) {
      try {
        const options = { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true };
        fileToUpload = await imageCompression(file, options);
      } catch (err) {
        console.warn("Gagal kompres gambar, upload file asli.", err);
      }
    }

    const base64Full = await fileToBase64(fileToUpload);
    const base64Data = base64Full.split(",")[1];
    const URL_GAS = process.env.REACT_APP_GOOGLE_SCRIPT_ACTION_URL;

    const payloadGAS = JSON.stringify({
      action: "uploadBukti",
      fileBase64: base64Data,
      mimeType: file.type,
    });
    const res = await axios.post(URL_GAS, payloadGAS, {
      headers: { "Content-Type": "text/plain" },
    });

    if (res.data.result === "success") return res.data.message.buktiUrl;
    else throw new Error("Gagal upload ke Google Drive");
  };

  const handleSubmit = async () => {
    if (!form.nama || batchList.length === 0) {
      showModal("Perhatian", "Pilih nama dan tambahkan minimal 1 data.", "error");
      return;
    }
    if (!drawing) {
      showModal("Perhatian", "Tanda tangan wajib diisi.", "error");
      return;
    }

    try {
      setLoading(true);
      setLoadingModal(true);
      setLoadingText("Sedang mengupload file bukti...");

      const finalTabunganList = [];

      for (let i = 0; i < batchList.length; i++) {
        const item = batchList[i];
        setLoadingText(`Mengupload file ${i + 1} dari ${batchList.length}...`);

        let urlBukti = "";

        if (item.bukti_file_obj) {
          try {
            urlBukti = await uploadSingleFileToGAS(item.bukti_file_obj);
          } catch (err) {
            console.error(`Gagal upload file untuk ${item.kerja_tabungan_gaji}`, err);
            throw new Error(`Gagal upload bukti untuk: ${item.kerja_tabungan_gaji}. Cek koneksi internet.`);
          }
        }

        const actualClean = item.actual.toString().replace(",", ".");
        finalTabunganList.push({
          kerja_tabungan_gaji: item.kerja_tabungan_gaji,
          hasil_berwujud: item.hasil_berwujud,
          konversi_uang: item.konversi_uang,
          parameter: item.parameter,
          target: item.target,
          nilai: item.nilai,
          bobot: item.bobot,
          actual: actualClean,
          bukti_nilai: urlBukti,
          bukti_nilai_name: item.bukti_nilai_name,
          bukti_pdf: item.bukti_pdf,
        });
      }

      setLoadingText("Menyimpan data ke server...");

      const payloadText = {
        email: user.email,
        password: user.password,
        nama: form.nama,
        divisi: form.divisi,
        unit: form.unit,
        tanda_tangan: drawing,
        tabungan_list: finalTabunganList,
        is_new_user: false, 
      };

      const res = await axios.post(`${API}/api/tabungan-batch`, payloadText);
      setLoadingModal(false);

      if (res.data.result === "success") {
        showFinishModal();
      } else {
        showModal("Gagal", res.data.message, "error");
      }
    } catch (err) {
      setLoadingModal(false);
      const serverMsg = err.response?.data?.message ? ` — ${err.response.data.message}` : "";
      showModal("Gagal", "Error submit: " + err.message + serverMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  // ========================= SIGNATURE LOGIC =========================
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawing("");
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    let rect = canvas.getBoundingClientRect();
    let x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    let y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);

    const move = (ev) => {
      let nx = (ev.touches ? ev.touches[0].clientX : ev.clientX) - rect.left;
      let ny = (ev.touches ? ev.touches[0].clientY : ev.clientY) - rect.top;
      ctx.lineTo(nx, ny);
      ctx.stroke();
    };

    const up = () => {
      setDrawing(canvas.toDataURL());
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      if (drawing) {
        const img = new Image();
        img.src = drawing;
        img.onload = () =>
          ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
      }
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [drawing]);

  const isItemSelesai = (kerjaTabungan, parameter) => {
    const inBatch = batchList.some(
      (b) => b.kerja_tabungan_gaji === kerjaTabungan && b.parameter === parameter,
    );
    if (inBatch) return true;

    const key = [form.divisi, kerjaTabungan, parameter].join("|").toLowerCase();
    return submittedTabungan.includes(key);
  };

  const handleActualChange = (e) => {
    let val = e.target.value.replace(".", ",");
    if (val === "" || /^[0-9,]*$/.test(val)) {
      if ((val.match(/,/g) || []).length <= 1)
        setForm({ ...form, actual: val });
    }
  };

  const formatBobot = (val) => {
    if (!val && val !== 0) return "";
    const num = Number(val);
    if (!isNaN(num) && num > 0 && num <= 1) {
      return Math.round(num * 100).toString();
    }
    return String(val).replace("%", "");
  };

  return (
    <>
      <div className="rsia-modal-backdrop">
        <div className="rsia-modal" role="dialog" aria-modal="true">
          <div className="rsia-modal-header">
            <h5 id="tabungan-title">Input Tabungan</h5>
            <button
              className="rsia-modal-close"
              onClick={onClose}
              disabled={loading}
            >
              &times;
            </button>
          </div>

          <div className="rsia-modal-body">
            {/* ===================== NAMA SEARCH (WIN-WIN SOLUTION) ===================== */}
            <div className="rsia-form-group" style={{ position: "relative" }} ref={namaDropdownRef}>
              <label>Nama Karyawan</label>
              <input
                ref={inputRef}
                name="nama"
                value={form.nama}
                onChange={handleNamaChange}
                onFocus={handleNamaFocus} /* 🔥 Memicu dropdown saat diklik 🔥 */
                placeholder="Ketik minimal 3 huruf..."
                autoComplete="off"
                disabled={loading}
              />
              
              {/* 🔥 Tampil selama dropdownOpen true 🔥 */}
              {dropdownOpen && (
                <ul className="rsia-dropdown" role="listbox">
                  {form.nama.length < 3 ? (
                    <li className="not-found-state">
                      <div className="not-found-text" style={{ color: "#64748b", fontStyle: "italic", fontSize: "0.9rem" }}>
                        Ketik minimal 3 huruf untuk memunculkan daftar nama...
                      </div>
                    </li>
                  ) : filteredNames.length > 0 ? (
                    filteredNames.map((nama, index) => (
                      <li key={index} onClick={() => handleSelectNama(nama)}>
                        <span className="rsia-nama">{nama}</span>
                        <small className="rsia-detail">
                          {masterData.find((d) => d.nama === nama)?.divisi} -{" "}
                          {masterData.find((d) => d.nama === nama)?.unit}
                        </small>
                      </li>
                    ))
                  ) : (
                    <li className="not-found-state">
                      <div className="not-found-text">
                        Data <b>"{form.nama}"</b> tidak ditemukan di Master Data.
                        <br/><small style={{color:"#64748b"}}>Gunakan tombol "Tambah Indikator Baru" di Halaman Utama.</small>
                      </div>
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* ===================== FORM DATA ===================== */}
            <div className="rsia-form-row">
              <div className="rsia-form-group">
                <label>Divisi</label>
                <input
                  name="divisi"
                  value={form.divisi}
                  onChange={handleChange}
                  readOnly
                  className="rsia-input-locked"
                  placeholder="Divisi"
                />
              </div>
              <div className="rsia-form-group">
                <label>Unit</label>
                <input
                  name="unit"
                  value={form.unit}
                  onChange={handleChange}
                  readOnly
                  className="rsia-input-locked"
                  placeholder="Unit"
                />
              </div>
            </div>

            <div className="rsia-form-group" style={{ position: "relative" }}>
              <label>Kerja Tabungan / Gaji</label>
                <>
                  <div
                    className={`rsia-premium-select ${tabunganOpen ? "open" : ""} ${!form.nama ? "rsia-input-locked" : ""}`}
                    onClick={() => {
                      if (form.nama) setTabunganOpen((o) => !o);
                    }}
                  >
                    <span
                      className={form.kerja_tabungan_gaji ? "" : "placeholder"}
                    >
                      {form.kerja_tabungan_gaji || "Pilih Item Tabungan"}
                    </span>
                    <span className="arrow">▾</span>
                  </div>
                  {tabunganOpen && (
                    <ul className="rsia-premium-dropdown" role="listbox">
                      {filteredList.map((item, index) => {
                        const selesai = isItemSelesai(
                          item.kerja_tabungan_gaji,
                          item.parameter,
                        );
                        return (
                          <li
                            key={index}
                            className={selesai ? "done" : ""}
                            onClick={() =>
                              !selesai && handleTabunganChange(item)
                            }
                          >
                            <span className="indikator-name">
                              {item.kerja_tabungan_gaji}
                            </span>
                            {selesai && (
                              <span className="done-badge">✓ Selesai</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
            </div>

            <div className="rsia-form-group">
              <label>Hasil Berwujud (Bukti Penilaian)</label>
              <input
                name="hasil_berwujud"
                value={form.hasil_berwujud}
                onChange={handleChange}
                readOnly
                className="rsia-input-locked"
                placeholder="Hasil Berwujud"
              />
            </div>

            <div className="rsia-form-row">
              <div className="rsia-form-group">
                <label>Parameter</label>
                <input
                  name="parameter"
                  value={form.parameter}
                  onChange={handleChange}
                  readOnly
                  className="rsia-input-locked"
                  placeholder="Parameter"
                />
              </div>
              <div className="rsia-form-group">
                <label>Target</label>
                <input
                  name="target"
                  value={form.target}
                  onChange={handleChange}
                  readOnly
                  className="rsia-input-locked"
                  placeholder="Target"
                />
              </div>
              <div className="rsia-form-group">
                <label>Konversi Uang</label>
                <input
                  name="konversi_uang"
                  value={form.konversi_uang}
                  onChange={handleChange}
                  readOnly
                  className="rsia-input-locked"
                  placeholder="Konversi Uang"
                />
              </div>
              <div className="rsia-form-group">
                <label>Bobot (%)</label>
                <input
                  name="bobot"
                  type="number"
                  value={form.bobot}
                  onChange={handleChange}
                  readOnly
                  className="rsia-input-locked"
                  placeholder="Bobot"
                />
              </div>
            </div>

            <div className="rsia-form-row">
              <div className="rsia-form-group">
                <label>Actual (Nilai)</label>
                <input
                  type="text"
                  name="actual"
                  value={form.actual}
                  onChange={handleActualChange}
                  inputMode="decimal"
                  disabled={loading || !form.kerja_tabungan_gaji}
                  placeholder="Actual"
                />
              </div>
            </div>

            {/* ===================== UPLOAD & BATCH ===================== */}
            <div className="rsia-form-group">
              <label>Upload Bukti (jpg/png/pdf)</label>
              <input
                type="file"
                ref={fileRef}
                onChange={handleFileChange}
                disabled={loading || !form.kerja_tabungan_gaji}
              />

              {buktiNilaiPreview && !buktiIsPDF && (
                <div className="rsia-preview-wrap">
                  <img
                    src={buktiNilaiPreview}
                    alt="preview"
                    className="rsia-preview"
                  />
                </div>
              )}
              {buktiIsPDF && (
                <div
                  className="rsia-preview-wrap"
                  style={{ marginTop: "10px" }}
                >
                  <div className="rsia-preview-title">
                    File PDF: {buktiNilaiName}
                  </div>
                  <iframe
                    src={buktiNilaiPreview}
                    title="PDF Preview"
                    style={{
                      width: "100%",
                      height: "400px",
                      border: "1px solid #ccc",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="rsia-form-group">
              <button
                type="button"
                className="add-batch"
                onClick={handleAddBatch}
                disabled={!form.kerja_tabungan_gaji || !form.actual || loading}
              >
                Tambah ke Daftar
              </button>
            </div>

            {batchList.length > 0 && (
              <div className="rsia-batch-list">
                <label>Daftar Tabungan yang akan dikirim</label>
                <ul>
                  {batchList.map((b, i) => (
                    <li key={i}>
                      <div className="batch-info">
                        <div className="indikator">{b.kerja_tabungan_gaji}</div>
                        <div className="meta">
                          Param: {b.parameter} • Nilai: {b.actual} • File:{" "}
                          {b.bukti_nilai_name}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="delete-batch"
                        onClick={() =>
                          setBatchList((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                        disabled={loading}
                      >
                        Hapus
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rsia-form-group">
              <label className="rsia-sign-label">Tanda Tangan</label>
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                onMouseDown={startDrawing}
                onTouchStart={startDrawing}
                style={{
                  border: "1px solid #e2e8f0",
                  width: "100%",
                  height: "200px",
                  touchAction: "none",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                }}
              />
              <div className="rsia-canvas-footer">
                <button
                  type="button"
                  className="clear-sign"
                  onClick={clearSignature}
                  disabled={loading}
                >
                  Hapus Tanda Tangan
                </button>
              </div>
            </div>
          </div>

          <div className="rsia-modal-footer">
            <button onClick={onClose} disabled={loading}>
              Batal
            </button>
            <button onClick={handleSubmit} disabled={loading}>
              Simpan Tabungan
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
        onClose={() => {
          setModalOpen(false);
          if (modalType === "finish") onClose();
        }}
      />
      <Modal
        isOpen={loadingModal}
        title="Menyimpan Data"
        message={loadingText}
        type="loading"
        isLoading={true}
        onClose={null}
      />
    </>
  );
}